import { gql } from '@apollo/client';

import { useMutation } from '@fhs/client';
import { UserAccountsDocument } from '@fhs-legacy/frontend/src/generated/rbi-graphql';
import { client } from '@fhs-legacy/frontend/src/state/graphql/client';
import { useLocale } from '@fhs-legacy/frontend/src/state/intl';
import { getCountryAndCurrencyCodes } from '@fhs-legacy/frontend/src/utils/form/constants';

type ReloadGiftCardInput = {
  amount: number;
  billingAddress?: {
    administrativeArea: string;
    locality: string;
    postalCode: string;
    regionCode: string;
    route: string;
    streetNumber: string;
  };
  currency?: string;
  paymentMethod: string;
  platform?: null;
};

const ReloadGiftCardMutation = gql`
  mutation ReloadGiftCard($input: ReloadGiftCardInput) @gateway @authRequired {
    reloadGiftCard(input: $input) {
      giftCardDetails {
        cardType
        currentBalance
        last4
      }
    }
  }
`;

export function useReloadGiftCardMutation({ onReloadSuccess }: { onReloadSuccess: () => void }) {
  const { feCountryCode } = useLocale();
  const { currencyCode } = getCountryAndCurrencyCodes(feCountryCode);

  return useMutation({
    mutationFn: async (input: ReloadGiftCardInput) => {
      const inputWithCurrency = {
        ...input,
        currency: currencyCode,
      };

      // Using Apollo client abstraction for now, but the idea is to replace this once we define how to hit Gateway without using Apollo.
      const { data } = await client.mutate({
        mutation: ReloadGiftCardMutation,
        fetchPolicy: 'no-cache',
        variables: { input: inputWithCurrency },
        refetchQueries: [{ query: UserAccountsDocument, variables: { feCountryCode } }], // We need to refetch UserAccounts when updating payment methods
      });

      return data?.addGiftCard;
    },
    onSuccess: () => {
      onReloadSuccess();
    },
  });
}
